import { zodResolver } from '@hookform/resolvers/zod'
import { TelInput } from '@xweather/theme'
import i18next from 'i18next'
import { type ValidatePhoneNumberLengthResult, validatePhoneNumberLength } from 'libphonenumber-js'
import { enqueueSnackbar } from 'notistack'
import { type ComponentProps, type ReactNode, useState } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { logout } from '../../api/helpers.ts'
import { Box } from '../../components/Box.tsx'
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Button,
	Collapse,
	Divider,
	Drawer,
	FormControlLabel,
	FormLabel,
	InputLabel,
	MenuItem,
	Paper,
	Switch,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '../../components/generic.tsx'
import { I18n } from '../../components/I18n.tsx'
import { Icon } from '../../components/Icon.tsx'
import { IconButton } from '../../components/IconButton.tsx'
import { Restricted } from '../../components/Restricted.tsx'
import { Stack } from '../../components/Stack.tsx'
import { TimeRangePicker } from '../../components/TimeRangePicker.tsx'
import {
	currentUserData,
	languages,
	preferenceOptions,
	type UserPreferences,
	UserPreferencesSchema,
	type UserProfileUpdate,
	UserProfileUpdateSchema,
	usePreferences,
	useStartEmailVerification,
	useStartPhoneVerification,
	useUpdatePreferences,
	useUpdateProfile,
	useVerifyPhoneNumber,
} from '../../data/userProfile.ts'
import type { InsightUser } from '../../data/users.ts'
import { ids } from '../../ids.ts'
import { useFlags } from '../../launchdarkly.ts'
import { RoleIcon } from '../../routes/_app/users/-roleUtils.tsx'
import { useDrawer } from '../../states.ts'
import { TimezonePicker } from './TimezonePicker.tsx'
import { getSystemTimezone } from './timezones.ts'

export const Preferences = () => {
	const { selectedDrawer, setSelectedDrawer } = useDrawer()
	const open = selectedDrawer === 'preferences'
	const closeDrawer = () => setSelectedDrawer(undefined)

	return (
		<Drawer open={open} anchor='right' onClose={closeDrawer}>
			<PreferencesContent minWidth={420} />
		</Drawer>
	)
}

export const PreferencesContent = (props: ComponentProps<typeof Box>) => {
	const { setSelectedDrawer } = useDrawer()
	const { data: preferences } = usePreferences()
	const { data: currentUser } = currentUserData.use()
	const { mutate: updatePreferences, isPending: preferencesPending } = useUpdatePreferences()
	const { mutate: updateProfile, isPending: profilePending } = useUpdateProfile()
	const closeDrawer = () => setSelectedDrawer(undefined)

	const profileForm = useForm<UserProfileUpdate>({
		resolver: zodResolver(UserProfileUpdateSchema),
		values: {
			name: currentUser.fullName,
			phone: currentUser.phone.phone,
		},
	})
	const preferencesForm = useForm<UserPreferences>({
		resolver: zodResolver(UserPreferencesSchema),
		values: preferences,
	})

	const onSubmit = (e: React.BaseSyntheticEvent) => {
		e.preventDefault()
		if (profileForm.formState.isDirty) {
			const profileValues = profileForm.getValues()
			updateProfile(profileValues)
		}
		if (preferencesForm.formState.isDirty) {
			const preferencesValues = preferencesForm.getValues()
			updatePreferences(preferencesValues)
		}
	}

	const isDirty = preferencesForm.formState.isDirty || profileForm.formState.isDirty
	const isSaving = preferencesPending || profilePending

	return (
		<Box component='form' flex col full onSubmit={onSubmit} {...props}>
			<PreferencesHeader closeDrawer={closeDrawer} />
			<Divider />
			<Box flex col overflow='auto'>
				<UserEmailSection user={currentUser} />
				<FormProvider {...profileForm}>
					<ProfileSection user={currentUser} />
				</FormProvider>
				<FormProvider {...preferencesForm}>
					<NotificationsSection />
					<UnitsSection />
					<AirQualitySection />
					<RegionalPreferencesSection />
					<AppearanceSection />
				</FormProvider>
			</Box>
			<PreferencesFooter closeDrawer={closeDrawer} isDirty={isDirty} isSaving={isSaving} />
		</Box>
	)
}

const PreferencesHeader = ({ closeDrawer }: { closeDrawer: () => void }) => {
	const { t } = useTranslation()
	return (
		<Box flex center justify='space-between' py={1} px={2}>
			<Typography variant='h1'>{t(($) => $.preferences.title)}</Typography>
			<Restricted mobile={false}>
				<IconButton data-ta={ids.preferences.close} icon='Close' label={t(($) => $.close)} onClick={closeDrawer} />
			</Restricted>
		</Box>
	)
}

const UserEmailSection = ({ user }: { user: InsightUser }) => {
	const { t } = useTranslation()
	const { mutate: verifyEmail } = useStartEmailVerification()
	const { intercomUserOnboarding } = useFlags()
	const showEmailVerification = !user.email.verified && !intercomUserOnboarding

	return (
		<>
			<Box flex center justify='space-between' px={2} pt={2} pb={showEmailVerification ? 0 : 2} gap={1}>
				<Typography variant='body1' sx={{ color: showEmailVerification ? 'error.main' : 'text.primary' }}>
					{user.email.email}
				</Typography>
				<Button
					data-ta={ids.preferences.logout}
					variant='text'
					size='small'
					onClick={() => logout(user.id)}
					startIcon={<Icon name='ArrowRightFromBracket' />}
				>
					{t(($) => $.navigation.logout)}
				</Button>
			</Box>
			{showEmailVerification && (
				<Box flex align='center' gap={1} px={2} pb={2}>
					<Typography variant='body2' sx={{ color: 'error.main' }}>
						({t(($) => $.preferences.unverified)})
					</Typography>
					<Button
						color='secondary'
						size='small'
						onClick={() => {
							verifyEmail(user.id)
							enqueueSnackbar({ message: t(($) => $.preferences.emailVerification.sent), variant: 'success' })
						}}
					>
						{t(($) => $.preferences.phoneVerification.verify)}
					</Button>
				</Box>
			)}
		</>
	)
}

const ProfileSection = ({ user }: { user: InsightUser }) => {
	const { t } = useTranslation()
	const { register } = useFormContext<UserProfileUpdate>()

	return (
		<Accordion defaultExpanded>
			<AccordionSummary data-ta={ids.preferences.profile.main}>{t(($) => $.preferences.profile)}</AccordionSummary>
			<AccordionDetails>
				<Box flex col gap={2}>
					<TextField
						data-ta={ids.preferences.profile.name}
						disabled={user.isSsoUser}
						label={t(($) => $.preferences.name)}
						size='small'
						required
						{...register('name')}
					/>
					<Box flex col gap={0.5}>
						<InputLabel>{t(($) => $.preferences.role)}</InputLabel>
						<Box flex align='center' gap={1}>
							<RoleIcon role={user.role} />
							<Typography data-ta={ids.preferences.profile.role}>{user.role.label}</Typography>
						</Box>
					</Box>
					<PhoneInputForm key={user.phone.phone} user={user} />
					{user.isSsoUser && (
						<Alert severity='info'>
							<I18n i18nKey={($) => $.sso.userInfoManaged} />
						</Alert>
					)}
				</Box>
			</AccordionDetails>
		</Accordion>
	)
}

const NotificationsSection = () => {
	const { t } = useTranslation()
	const { control, watch } = useFormContext<UserPreferences>()
	const { data: user } = currentUserData.use()
	const hasPhone = !!user.phone

	return (
		<Accordion>
			<AccordionSummary data-ta={ids.preferences.notifications.main}>{t(($) => $.preferences.notifications)}</AccordionSummary>
			<AccordionDetails>
				<Box flex col gap={2}>
					<Stack col>
						<Controller
							name='notificationsSound'
							control={control}
							render={({ field }) => (
								<FormControlLabel
									data-ta={ids.preferences.notifications.browser}
									control={
										<Switch
											onChange={() => {
												field.onChange(field.value === 'ALL' ? 'NONE' : 'ALL')
											}}
											checked={field.value === 'ALL'}
										/>
									}
									label={t(($) => $.preferences.notification.audio)}
								/>
							)}
						/>
						<Collapse in={watch('notificationsSound') === 'ALL'} mountOnEnter unmountOnExit>
							<Box flex col>
								<InputLabel>{t(($) => $.preferences.notification.audioLightningRings)}</InputLabel>
								<Controller
									name='notificationsSoundLightningRings'
									control={control}
									render={({ field }) => (
										<ToggleButtonGroup
											data-ta={ids.preferences.notifications.browserLightningRings}
											exclusive
											fullWidth
											size='large'
											value={field.value}
											onChange={(_, newValue) => newValue && field.onChange(newValue)}
										>
											{preferenceOptions.lightningRings.map((opt) => (
												<ToggleButton data-ta={`${ids.preferences.notifications.browserLightningRings}-${opt}`} key={opt} value={opt}>
													<I18n i18nKey={($) => $.preferences.notification.audioLightningRingLabels[opt]} />
												</ToggleButton>
											))}
										</ToggleButtonGroup>
									)}
								/>
							</Box>
						</Collapse>
					</Stack>
					<Controller
						name='notificationsEmail'
						control={control}
						render={({ field }) => (
							<FormControlLabel
								data-ta={ids.preferences.notifications.email}
								control={
									<Switch
										onChange={() => {
											field.onChange(field.value === 'ALL' ? 'NONE' : 'ALL')
										}}
										checked={field.value === 'ALL'}
									/>
								}
								label={t(($) => $.preferences.notification.email)}
							/>
						)}
					/>
					<Controller
						name='notificationsSms'
						control={control}
						render={({ field }) => (
							<FormControlLabel
								data-ta={ids.preferences.notifications.sms}
								control={
									<Switch
										onChange={() => {
											field.onChange(field.value === 'ALL' ? 'NONE' : 'ALL')
										}}
										checked={field.value === 'ALL'}
									/>
								}
								label={t(($) => $.preferences.notification.sms)}
								disabled={!hasPhone}
							/>
						)}
					/>
					<Controller
						name='notificationsSnoozeSms'
						control={control}
						render={({ field }) => (
							<>
								<FormControlLabel
									data-ta={ids.preferences.notifications.snooze.main}
									control={
										<Switch
											data-ta={ids.preferences.notifications.snooze.switch}
											onChange={(_e, checked) => {
												field.onChange(checked ? field.value || '09:00_17:00' : '')
											}}
											checked={field.value !== ''}
										/>
									}
									label={t(($) => $.preferences.notification.snoozeSms)}
									disabled={watch('notificationsSms') !== 'ALL'}
								/>
								{field.value && (
									<TimeRangePicker
										onChange={(startValue, endValue) => {
											field.onChange(`${startValue}_${endValue}`)
										}}
										startValue={field.value.split('_')[0] || '09:00'}
										endValue={field.value.split('_')[1] || '17:00'}
										startLabel={t(($) => $.preferences.notification.startSnooze)}
										endLabel={t(($) => $.preferences.notification.endSnooze)}
										disabled={watch('notificationsSms') !== 'ALL'}
									/>
								)}
							</>
						)}
					/>
				</Box>
			</AccordionDetails>
		</Accordion>
	)
}

const UnitsSection = () => {
	const { t } = useTranslation()
	return (
		<Accordion>
			<AccordionSummary data-ta={ids.preferences.units.main}>{t(($) => $.preferences.units)}</AccordionSummary>
			<AccordionDetails>
				<Box flex col gap={2}>
					<UnitSelection name='unitsDistance' />
					<UnitSelection name='unitsAirTemperature' />
					<UnitSelection name='unitsWindSpeed' />
					<UnitSelection name='unitsAirPressure' />
					<UnitSelection name='unitsRain' />
					<UnitSelection name='unitsOtherGases' />
				</Box>
			</AccordionDetails>
		</Accordion>
	)
}

const AirQualitySection = () => {
	const { t } = useTranslation()
	return (
		<Accordion>
			<AccordionSummary data-ta={ids.preferences.airQualityIndex.main}>{t(($) => $.preferences.airQualityIndex)}</AccordionSummary>
			<AccordionDetails>
				<UnitSelection name='unitsAirQuality' />
			</AccordionDetails>
		</Accordion>
	)
}

const RegionalPreferencesSection = () => {
	const { t } = useTranslation()
	const { register, watch, setValue } = useFormContext<UserPreferences>()
	const { portugueseLanguage } = useFlags()
	const availableLanguages = languages.filter((lng) => lng !== 'pt-BR' || portugueseLanguage)

	return (
		<Accordion>
			<AccordionSummary data-ta={ids.preferences.regionalPreferences.main}>{t(($) => $.preferences.regionalPreferences)}</AccordionSummary>
			<AccordionDetails>
				<Box flex col gap={2}>
					<TextField
						data-ta={ids.preferences.regionalPreferences.format.dropDown}
						select
						label={t(($) => $.preferences.dateFormat)}
						{...register('dateFormat')}
						value={watch('dateFormat')}
					>
						<MenuItem data-ta={`${ids.preferences.regionalPreferences.format.dropDown}-DD.MM.YYYY`} value='DAY_MONTH_YEAR'>
							{t(($) => $.preferences.dateFormats.DAY_MONTH_YEAR)}
						</MenuItem>
						<MenuItem data-ta={`${ids.preferences.regionalPreferences.format.dropDown}-MM.DD.YYYY`} value='MONTH_DAY_YEAR'>
							{t(($) => $.preferences.dateFormats.MONTH_DAY_YEAR)}
						</MenuItem>
					</TextField>
					<TextField
						data-ta={ids.preferences.regionalPreferences.language.main}
						select
						label={t(($) => $.preferences.language)}
						{...register('language')}
						value={watch('language')}
					>
						{availableLanguages.map((lng) => (
							<MenuItem data-ta={ids.preferences.regionalPreferences.language.option(lng)} key={lng} value={lng}>
								{t(($) => $.preferences.languages[lng])}
							</MenuItem>
						))}
					</TextField>
					<Box data-ta={ids.preferences.regionalPreferences.timezone.main} flex col gap={0.5}>
						<Box flex align='center' justify='space-between'>
							<InputLabel>
								<I18n i18nKey={($) => $.preferences.timezone} />
							</InputLabel>
							<Button
								data-ta={ids.preferences.regionalPreferences.timezone.autodetect}
								variant='text'
								size='small'
								onClick={() => setValue('timezone', getSystemTimezone(), { shouldDirty: true })}
							>
								{t(($) => $.preferences.timezoneAutodetect)}
							</Button>
						</Box>
						<Controller
							name='timezone'
							render={({ field }) => (
								<TimezonePicker
									dataTa={ids.preferences.regionalPreferences.timezone.dropDown}
									value={field.value}
									onChange={field.onChange}
								/>
							)}
						/>
					</Box>
				</Box>
			</AccordionDetails>
		</Accordion>
	)
}

const AppearanceSection = () => {
	const { t } = useTranslation()
	return (
		<Accordion>
			<AccordionSummary data-ta={ids.preferences.appearance.main}>{t(($) => $.preferences.appearance)}</AccordionSummary>
			<AccordionDetails>
				<UnitSelection name='theme' />
			</AccordionDetails>
		</Accordion>
	)
}

const PreferencesFooter = ({ closeDrawer, isDirty, isSaving }: { closeDrawer: () => void; isDirty: boolean; isSaving: boolean }) => {
	const { t } = useTranslation()
	return (
		<Box flex col flexGrow={1} justify='flex-end'>
			<Divider />
			<Box flex p={2} gap={1}>
				<Restricted mobile={false}>
					<Button data-ta={ids.preferences.cancel} variant='text' onClick={closeDrawer}>
						{t(($) => $.cancel)}
					</Button>
				</Restricted>
				<Button data-ta={ids.preferences.save} type='submit' disabled={!isDirty || isSaving}>
					{isSaving ? t(($) => $.saving) : t(($) => $.save)}
				</Button>
			</Box>
		</Box>
	)
}

const PhoneInputForm = ({ user }: { user: InsightUser }) => {
	const [verificationCode, setVerificationCode] = useState('')
	const [phone, setPhone] = useState(user.phone.phone)
	const [errorCode, setErrorCode] = useState<ValidatePhoneNumberLengthResult>()
	const [verification, setVerification] = useState<'needed' | 'verifying'>()
	const startPhoneVerification = useStartPhoneVerification()
	const verifyPhoneNumber = useVerifyPhoneNumber()
	const updateProfile = useUpdateProfile()
	const { data: preferences } = usePreferences()
	const { mutate: updatePreferences } = useUpdatePreferences()

	const verify = () => {
		verifyPhoneNumber.mutate(
			{ phone, otp: verificationCode.trim() },
			{
				onSuccess: () => {
					updateProfile.mutate(
						{ name: user.fullName, phone },
						{
							onSuccess: () => {
								setVerificationCode('')
								setVerification(undefined)
								enqueueSnackbar({ variant: 'success', message: <I18n i18nKey={($) => $.preferences.phoneVerification.phoneVerified} /> })
							},
						},
					)
				},
				onError: () => enqueueSnackbar({ variant: 'error', message: <I18n i18nKey={($) => $.preferences.phoneVerification.failed} /> }),
			},
		)
	}

	const hasError = phone.length > 0 && !!errorCode
	const canRemovePhone = user.phone.phone !== '' && verification === undefined

	const removePhone = () => {
		updateProfile.mutate(
			{ name: user.fullName, phone: undefined },
			{
				onSuccess: () => {
					if (preferences && (preferences.notificationsSms === 'ALL' || preferences.notificationsSnoozeSms)) {
						updatePreferences({ ...preferences, notificationsSms: 'NONE', notificationsSnoozeSms: '' })
					}
					enqueueSnackbar({ variant: 'success', message: <I18n i18nKey={($) => $.preferences.phoneVerification.phoneRemoved} /> })
				},
			},
		)
	}

	return (
		<Stack col>
			<FormLabel htmlFor='phone'>
				<I18n i18nKey={($) => $.preferences.phone} />
			</FormLabel>
			<Stack>
				<TelInput
					data-ta={ids.preferences.profile.phone}
					name='phone'
					value={phone}
					onChange={(value) => {
						const newPhone = value.replace(/\s+/g, '')
						setPhone(newPhone)
						const errorCode = validatePhoneNumberLength(newPhone)
						setErrorCode(errorCode)
						setVerification(!errorCode && newPhone !== user.phone.phone ? 'needed' : undefined)
					}}
					excludedCountries={['RU', 'BY', 'KP', 'AX']}
					error={hasError}
					helperText={
						hasError ? (
							<I18n i18nKey={($) => $.preferences.phoneValidation[errorCode]} />
						) : (
							verification === 'needed' && <I18n i18nKey={($) => $.preferences.phoneVerification.notVerified} />
						)
					}
					fullWidth
					disabled={user.isSsoUser || verification === 'verifying'}
				/>
				{canRemovePhone && (
					<IconButton
						data-ta={ids.preferences.profile.removePhoneBtn}
						color='error'
						icon='Trash'
						label={i18next.t(($) => $.preferences.phoneVerification.removePhone)}
						onClick={removePhone}
					/>
				)}
				{verification === 'needed' && (
					<Button
						data-ta={ids.preferences.profile.verifyPhoneBtn}
						onClick={() => {
							setVerification('verifying')
							startPhoneVerification.mutate(phone)
						}}
					>
						<I18n i18nKey={($) => $.preferences.phoneVerification.verify} />
					</Button>
				)}
				{verification === 'verifying' && (
					<Button
						data-ta={ids.preferences.profile.cancelVerifyBtn}
						variant='text'
						onClick={() => {
							setVerification(undefined)
							setVerificationCode('')
							setPhone(user.phone.phone)
						}}
					>
						<I18n i18nKey={($) => $.cancel} />
					</Button>
				)}
			</Stack>
			{verification === 'verifying' && (
				<Paper sx={({ palette }) => ({ p: 2, background: palette.background.paper1 })}>
					<FormLabel htmlFor='verificationCode'>
						<I18n i18nKey={($) => $.preferences.phoneVerification.codeLabel} />
					</FormLabel>
					<Stack>
						<TextField
							name='verificationCode'
							data-ta={ids.preferences.profile.code}
							placeholder={i18next.t(($) => $.preferences.phoneVerification.codePlaceholder)}
							onChange={(e) => setVerificationCode(e.target.value)}
							value={verificationCode}
						/>
						<Button onClick={() => verify()}>
							<I18n i18nKey={($) => $.preferences.phoneVerification.codeButton} />
						</Button>
					</Stack>
				</Paper>
			)}
		</Stack>
	)
}

type UnitName = Exclude<keyof typeof preferenceOptions, 'notification' | 'dateFormat' | 'lightningRings'>
const translationMap = {
	unitsDistance: <I18n i18nKey={($) => $.preferences.unit.unitsDistance} />,
	unitsAirTemperature: <I18n i18nKey={($) => $.preferences.unit.unitsAirTemperature} />,
	unitsWindSpeed: <I18n i18nKey={($) => $.preferences.unit.unitsWindSpeed} />,
	unitsAirPressure: <I18n i18nKey={($) => $.preferences.unit.unitsAirPressure} />,
	unitsRain: <I18n i18nKey={($) => $.preferences.unit.unitsRain} />,
	unitsOtherGases: <I18n i18nKey={($) => $.preferences.unit.unitsOtherGases} />,
	unitsAirQuality: <I18n i18nKey={($) => $.preferences.unit.unitsAirQuality} />,
	theme: <I18n i18nKey={($) => $.preferences.unit.theme} />,
} satisfies Record<UnitName, ReactNode>

const UnitSelection = ({ name }: { name: UnitName }) => {
	const { control, setValue } = useFormContext()
	const options = preferenceOptions[name]
	return (
		<Box flex col>
			<InputLabel>{translationMap[name]}</InputLabel>
			<Controller
				control={control}
				name={name}
				render={({ field }) => (
					<ToggleButtonGroup
						data-ta={`${ids.preferences.units.main}-${name}`}
						exclusive
						fullWidth
						size='small'
						value={field.value}
						onChange={(_, newValue) => newValue && setValue(name, newValue, { shouldDirty: true })}
					>
						{options.map((opt) => (
							<ToggleButton data-ta={`${ids.preferences.units.main}-${opt}`} key={opt} value={opt}>
								<I18n i18nKey={($) => $.preferences.unitLabels[opt]} />
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				)}
			/>
		</Box>
	)
}
